import { z } from 'zod'

import { quadrantSchema } from '@/lib/scoring/presentation'

import { apiClient } from '../client'
import { canopyPath } from '../routes'

export const dotSchema = z.looseObject({
  x: z.number(),
  y: z.number(),
})

export const displayScoresSchema = z.looseObject({
  def: z.number(),
  rdy: z.number(),
  defRaw: z.number(),
  rdyRaw: z.number(),

  urgency: z.number(),
  quadrant: quadrantSchema,
  dot: dotSchema,
})

export type DisplayScores = z.infer<typeof displayScoresSchema>

export const portfolioRowSchema = z.looseObject({
  id: z.string(),
  name: z.string(),
  sector: z.string().nullish(),
  status: z.string().nullish(),
  arrBand: z.string().nullish(),
  fund: z.string().nullish(),
  serentFund: z.string().nullish(),
  onboardingStep: z.number().int().nullish(),

  initials: z.string(),
  snapshotId: z.string().nullish(),
  snapshotDate: z.string().nullish(),
  source: z.string().nullish(),
  scores: displayScoresSchema.nullable(),
  topAction: z.string().nullish(),
})

export type PortfolioRow = z.infer<typeof portfolioRowSchema>

export const portfolioMetricsSchema = z.looseObject({
  totalCompanies: z.number().int(),
  scoredCompanies: z.number().int(),
  pendingCompanies: z.number().int(),
  avgDefensibility: z.number(),
  avgReadiness: z.number(),
  operationalEdgeCount: z.number().int(),
  emergencyUpgradeCount: z.number().int(),
})

export type PortfolioMetrics = z.infer<typeof portfolioMetricsSchema>

export const portfolioOverviewSchema = z.looseObject({
  quarters: z.array(z.string()),

  quarter: z.string().nullish(),
  metrics: portfolioMetricsSchema,
  companies: z.array(portfolioRowSchema),
})

export type PortfolioOverview = z.infer<typeof portfolioOverviewSchema>

export const PORTFOLIO_SORT_FIELDS = [
  'name',
  'sector',
  'quadrant',
  'urgency',
  'def',
  'rdy',
] as const

export type PortfolioSortField = (typeof PORTFOLIO_SORT_FIELDS)[number]
export type SortDirection = 'asc' | 'desc'

export interface PortfolioOverviewOptions {
  quarter?: string | undefined

  sort?: PortfolioSortField | undefined

  direction?: SortDirection | undefined
  signal?: AbortSignal | undefined
}

export function getPortfolioOverview(
  options: PortfolioOverviewOptions = {},
): Promise<PortfolioOverview> {
  const { quarter, sort, direction, signal } = options

  return apiClient().get(canopyPath('/portfolio/overview'), {
    schema: portfolioOverviewSchema,
    query: { quarter, sort, direction },
    ...(signal ? { signal } : {}),
  })
}

export const portfolioQuartersSchema = z.looseObject({
  quarters: z.array(z.string()),
  latest: z.string().nullish(),
})

export type PortfolioQuarters = z.infer<typeof portfolioQuartersSchema>

export function getPortfolioQuarters(signal?: AbortSignal): Promise<PortfolioQuarters> {
  return apiClient().get(canopyPath('/portfolio/quarters'), {
    schema: portfolioQuartersSchema,
    ...(signal ? { signal } : {}),
  })
}
